import { api } from '../../../shared/lib/axios';

/** Desligamento e reintegração (change `pessoas-desligamento-reintegracao`). */

export interface TemplateTask {
  id?: string;
  area: string;
  title: string;
  required: boolean;
  assigneeKind: 'gestor' | 'usuario' | 'rh';
  assigneeUserId?: string | null;
  sortOrder?: number;
}

export interface TerminationTemplate {
  id: string;
  name: string;
  employmentType: 'clt' | 'pj';
  active: boolean;
  tasks: TemplateTask[];
}

export interface TerminationTask {
  id: string;
  area: string;
  title: string;
  required: boolean;
  status: 'pendente' | 'concluida' | 'dispensada';
  systemKey: string | null;
  assigneeUserId: string | null;
  assigneeName: string | null;
  notes: string | null;
  doneByName: string | null;
  doneAt: string | null;
}

export interface Termination {
  id: string;
  bondId: string;
  type: string;
  typeLabel: string;
  reason: string;
  terminationDate: string;
  lastWorkingDay: string;
  noticeKind: string;
  noticeDate: string | null;
  notes: string | null;
  status: 'em_andamento' | 'concluido' | 'cancelado';
  cancelledReason: string | null;
  tasks: TerminationTask[];
}

export interface StartTerminationPayload {
  templateId: string;
  type: string;
  reason: string;
  terminationDate: string;
  lastWorkingDay: string;
  noticeKind: string;
  noticeDate?: string;
  notes?: string;
}

export interface MyTask {
  id: string;
  area: string;
  title: string;
  required: boolean;
  terminationId: string;
  terminationDate: string;
  userId: string;
  personName: string;
  internalRegistration: string;
}

export interface ReinstatementReason {
  id: string;
  code: string;
  name: string;
  requiresDocument: boolean;
  requiresLegalApproval: boolean;
  active: boolean;
}

export interface ReinstatementItem {
  id: string;
  bondId: string;
  userId: string;
  personName: string;
  internalRegistration: string;
  reason: { id: string; code: string; name: string; requiresLegalApproval: boolean };
  justification: string;
  documentId: string | null;
  reinstatementDate: string;
  status: 'aguardando_aprovacao' | 'complementacao_solicitada' | 'aprovada' | 'rejeitada' | 'cancelada';
  currentStep: 'rh' | 'juridico' | null;
  requestedByName: string | null;
  cancelledReason: string | null;
  approvals: Array<{ cycle: number; step: string; decision: string; comment: string | null; decidedAt: string; decidedByName: string | null }>;
}

function list<T>(body: unknown): T[] {
  if (Array.isArray(body)) return body as T[];
  const data = (body as { data?: unknown } | null)?.data;
  return Array.isArray(data) ? (data as T[]) : [];
}

function object<T>(body: unknown): T {
  const { version: _v, _links: _l, ...rest } = (body ?? {}) as Record<string, unknown>;
  return rest as T;
}

export async function listarModelos(all = false): Promise<TerminationTemplate[]> {
  const { data } = await api.get('/termination-templates', { params: { all: String(all) } });
  return list<TerminationTemplate>(data);
}

export async function salvarModelo(id: string | null, payload: { name: string; employmentType: string; tasks: TemplateTask[] }) {
  const { data } = id ? await api.patch(`/termination-templates/${id}`, payload) : await api.post('/termination-templates', payload);
  return object<TerminationTemplate>(data);
}

export async function situacaoModelo(id: string, active: boolean) {
  const { data } = await api.patch(`/termination-templates/${id}/active`, { active });
  return object<TerminationTemplate>(data);
}

export async function desligamentosDaPessoa(personId: string): Promise<Termination[]> {
  const { data } = await api.get(`/collaborators/${personId}/terminations`);
  return list<Termination>(data);
}

export async function iniciarDesligamento(bondId: string, payload: StartTerminationPayload): Promise<Termination> {
  const { data } = await api.post(`/employment-bonds/${bondId}/terminations`, payload);
  return object<Termination>(data);
}

export async function concluirDesligamento(id: string): Promise<Termination> {
  const { data } = await api.post(`/terminations/${id}/conclude`);
  return object<Termination>(data);
}

export async function cancelarDesligamento(id: string, justification: string): Promise<Termination> {
  const { data } = await api.post(`/terminations/${id}/cancel`, { justification });
  return object<Termination>(data);
}

export async function agirNaTarefa(taskId: string, action: 'concluir' | 'dispensar', notes?: string): Promise<Termination> {
  const { data } = await api.post(`/termination-tasks/${taskId}`, { action, notes });
  return object<Termination>(data);
}

export async function minhasTarefas(): Promise<MyTask[]> {
  const { data } = await api.get('/termination-tasks/mine');
  return list<MyTask>(data);
}

export async function listarMotivosReintegracao(all = false): Promise<ReinstatementReason[]> {
  const { data } = await api.get('/reinstatement-reasons', { params: { all: String(all) } });
  return list<ReinstatementReason>(data);
}

export async function salvarMotivoReintegracao(id: string | null, payload: Partial<ReinstatementReason>) {
  const { data } = id ? await api.patch(`/reinstatement-reasons/${id}`, payload) : await api.post('/reinstatement-reasons', payload);
  return object<ReinstatementReason>(data);
}

export async function reintegracoesDaPessoa(personId: string): Promise<ReinstatementItem[]> {
  const { data } = await api.get(`/collaborators/${personId}/reinstatements`);
  return list<ReinstatementItem>(data);
}

export async function solicitarReintegracao(
  bondId: string, payload: { reasonId: string; justification: string; reinstatementDate: string; documentId?: string },
): Promise<ReinstatementItem> {
  const { data } = await api.post(`/employment-bonds/${bondId}/reinstatements`, payload);
  return object<ReinstatementItem>(data);
}

export async function reintegracoesPendentes(): Promise<ReinstatementItem[]> {
  const { data } = await api.get('/reinstatements/pending');
  return list<ReinstatementItem>(data);
}

export async function decidirReintegracao(id: string, decision: string, comment?: string): Promise<ReinstatementItem> {
  const { data } = await api.post(`/reinstatements/${id}/decide`, { decision, comment });
  return object<ReinstatementItem>(data);
}

export async function reenviarReintegracao(id: string, justification: string, documentId?: string): Promise<ReinstatementItem> {
  const { data } = await api.post(`/reinstatements/${id}/resubmit`, { justification, documentId });
  return object<ReinstatementItem>(data);
}

export async function cancelarReintegracao(id: string, justification: string): Promise<ReinstatementItem> {
  const { data } = await api.post(`/reinstatements/${id}/cancel`, { justification });
  return object<ReinstatementItem>(data);
}
